/* Perfectly Cordial 2026 theme: the real Shopify cart, in the approved drawer design.
   Talks to the Ajax Cart API (/cart/add.js, /cart/change.js, /cart/update.js, /cart.js)
   and re-renders the drawer (and the cart page, when present) through the Section
   Rendering API, so Liquid stays the only place cart markup is written.

   CONTRACT for other scripts (documented in docs/shopify-theme-notes.md):
     window.PCCart.add(items, options)  items: [{ id, quantity, properties }] (id = variant id)
                                        options: { open: true }  resolves with the cart JSON
     window.PCCart.change(line, quantity)  line: 1-based line number OR line item key
     window.PCCart.update(updates)      updates: { "<variantId or key>": quantity, ... } or [qty, qty]
     window.PCCart.refresh()            re-fetches the cart and re-renders
     window.PCCart.open() / close() / isOpen()
     window.PCCart.get()                last cart JSON seen (or null)
     window.PCCart.formatMoney(cents)   uses the shop's money format
   Events on document:
     "pc:cart:updated"  detail { cart, source }   after every successful change
     "pc:cart:error"    detail { message, description, status }
     "pc:cart:open" / "pc:cart:close"
   Markup hooks: [data-cart-open], [data-cart-close], [data-cart-count],
     [data-pc-add] (data-variant-id, data-quantity, data-properties JSON),
     form[data-pc-add-form] (a product form posted to /cart/add.js),
     [data-cart-qty] (data-line, data-d), [data-cart-remove] (data-line),
     input[data-cart-line-qty] (data-line). */
(function () {
  "use strict";

  var PC = window.PC || {};
  var routes = PC.routes || {};
  var settings = PC.settings || {};
  var strings = PC.strings || {};
  var root = (routes.root || "/").replace(/\/$/, "");
  var drawer = document.querySelector("[data-cart-drawer]");
  var overlay = document.querySelector("[data-cart-overlay]");
  var drawerSection = drawer ? drawer.getAttribute("data-section-id") : "cart-drawer";
  var cartPage = document.querySelector("[data-cart-page]");
  var cartPageSection = cartPage ? cartPage.getAttribute("data-section-id") : null;
  var lastCart = null;
  var busy = false;

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }
  function toast(message) { emit("pc:toast", { message: message }); }

  function formatMoney(cents) {
    var format = PC.moneyFormat || "${{amount}}";
    var value = (Number(cents) || 0) / 100;
    var out = value.toFixed(2);
    if (/amount_no_decimals/.test(format)) out = Math.round(value).toString();
    if (/amount_with_comma_separator/.test(format)) out = out.replace(".", ",");
    out = out.replace(/\B(?=(\d{3})+(?!\d))/g, /comma_separator/.test(format) ? "." : ",");
    return format.replace(/\{\{\s*[a-z_]+\s*\}\}/, out);
  }

  function sectionIds() {
    var ids = [drawerSection];
    if (cartPageSection) ids.push(cartPageSection);
    return ids.join(",");
  }

  function request(path, body, method) {
    var opts = {
      method: method || "POST",
      headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
    };
    if (body instanceof FormData) {
      opts.body = body;
    } else if (body) {
      opts.headers["Content-Type"] = "application/json";
      opts.body = JSON.stringify(body);
    }
    return fetch(root + path, opts).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok) {
          var err = new Error(json.description || json.message || strings.cartError || "Something went wrong.");
          err.status = res.status;
          err.message = json.message || err.message;
          err.description = json.description || err.message;
          throw err;
        }
        return json;
      });
    });
  }

  function swapSections(sections) {
    if (!sections) return;
    var parser = new DOMParser();
    var html, doc, next, here;
    if (drawer && sections[drawerSection]) {
      html = sections[drawerSection];
      doc = parser.parseFromString(html, "text/html");
      next = doc.querySelector("[data-cart-body]");
      here = drawer.querySelector("[data-cart-body]");
      if (next && here) here.innerHTML = next.innerHTML;
    }
    if (cartPage && cartPageSection && sections[cartPageSection]) {
      html = sections[cartPageSection];
      doc = parser.parseFromString(html, "text/html");
      next = doc.querySelector("[data-cart-page-body]");
      here = cartPage.querySelector("[data-cart-page-body]");
      if (next && here) here.innerHTML = next.innerHTML;
    }
  }

  function setCount(cart) {
    if (!cart) return;
    var count = cart.item_count || 0;
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      if (el.textContent !== String(count)) {
        el.textContent = count;
        el.classList.remove("bump");
        void el.offsetWidth;
        el.classList.add("bump");
      }
    });
  }

  function setBusy(on) {
    busy = on;
    if (drawer) drawer.setAttribute("aria-busy", on ? "true" : "false");
    if (cartPage) cartPage.setAttribute("aria-busy", on ? "true" : "false");
    var controls = document.querySelectorAll("[data-cart-qty],[data-cart-remove],[data-cart-line-qty]");
    controls.forEach(function (c) { c.disabled = on; });
  }

  function showError(err, where) {
    var message = (err && (err.description || err.message)) || strings.cartError || "Something went wrong.";
    var target = where || (drawer && drawer.querySelector("[data-cart-error]"));
    if (target) target.textContent = message;
    if (cartPage) {
      var pageErr = cartPage.querySelector("[data-cart-error]");
      if (pageErr && pageErr !== target) pageErr.textContent = message;
    }
    emit("pc:cart:error", { message: err && err.message, description: message, status: err && err.status });
    return message;
  }

  function clearErrors() {
    document.querySelectorAll("[data-cart-error]").forEach(function (el) { el.textContent = ""; });
  }

  function fetchCart() {
    return request("/cart.js", null, "GET");
  }

  function fetchSections() {
    return fetch(root + "/?sections=" + encodeURIComponent(sectionIds()), {
      headers: { "Accept": "application/json" }
    }).then(function (res) { return res.ok ? res.json() : {}; }).catch(function () { return {}; });
  }

  /* remember which control had focus so a re-render does not drop the user */
  function focusMemo() {
    var a = document.activeElement;
    if (!a) return null;
    var line = a.getAttribute && a.getAttribute("data-line");
    if (!line) return null;
    var kind = a.hasAttribute("data-cart-remove") ? "remove" : a.hasAttribute("data-cart-qty") ? "qty" : a.hasAttribute("data-cart-line-qty") ? "input" : null;
    if (!kind) return null;
    var scope = a.closest("[data-cart-drawer]") ? "drawer" : "page";
    return { line: line, kind: kind, d: a.getAttribute("data-d"), scope: scope };
  }
  function focusRestore(memo, cart) {
    if (!memo) return;
    var scope = memo.scope === "drawer" ? drawer : cartPage;
    if (!scope) return;
    var sel = memo.kind === "remove" ? "[data-cart-remove][data-line='" + memo.line + "']"
      : memo.kind === "input" ? "[data-cart-line-qty][data-line='" + memo.line + "']"
      : "[data-cart-qty][data-line='" + memo.line + "'][data-d='" + memo.d + "']";
    var el = scope.querySelector(sel);
    if (!el && cart && cart.item_count) {
      /* the line went away: land on the line that took its place, else the close button */
      var prev = Math.max(1, Math.min(+memo.line, cart.item_count));
      el = scope.querySelector("[data-cart-remove][data-line='" + prev + "']");
    }
    if (!el) el = scope.querySelector("[data-cart-close]") || scope.querySelector("[data-cart-checkout]");
    if (el) el.focus({ preventScroll: true });
  }

  function applyCart(cart, sections, source) {
    var memo = focusMemo();
    lastCart = cart;
    swapSections(sections);
    setCount(cart);
    focusRestore(memo, cart);
    emit("pc:cart:updated", { cart: cart, source: source || "change" });
    return cart;
  }

  function refresh() {
    return Promise.all([fetchCart(), fetchSections()]).then(function (r) {
      return applyCart(r[0], r[1], "refresh");
    });
  }

  /* ---------------- public actions ---------------- */
  function add(items, options) {
    options = options || {};
    if (!Array.isArray(items)) items = [items];
    items = items.map(function (it) {
      var out = { id: Number(it.id), quantity: Math.max(1, parseInt(it.quantity, 10) || 1) };
      if (it.properties && Object.keys(it.properties).length) out.properties = it.properties;
      if (it.selling_plan) out.selling_plan = it.selling_plan;
      return out;
    });
    clearErrors();
    setBusy(true);
    return request("/cart/add.js", { items: items, sections: sectionIds() })
      .then(function (added) {
        return fetchCart().then(function (cart) {
          setBusy(false);
          applyCart(cart, added.sections, "add");
          var shouldOpen = options.open !== undefined ? options.open : settings.cartOpenOnAdd !== false;
          if (options.toast !== false) toast(options.message || strings.addedToCart || "Added to your cart.");
          if (shouldOpen) open();
          return cart;
        });
      })
      .catch(function (err) {
        setBusy(false);
        showError(err);
        if (options.open !== false && drawer) open();
        throw err;
      });
  }

  function addForm(form) {
    var data = new FormData(form);
    data.append("sections", sectionIds());
    clearErrors();
    setBusy(true);
    return request("/cart/add.js", data)
      .then(function (added) {
        return fetchCart().then(function (cart) {
          setBusy(false);
          applyCart(cart, added.sections, "add");
          toast(form.getAttribute("data-added-message") || strings.addedToCart || "Added to your cart.");
          if (settings.cartOpenOnAdd !== false) open();
          return cart;
        });
      })
      .catch(function (err) {
        setBusy(false);
        var local = form.querySelector("[data-cart-error]");
        showError(err, local);
        if (!local && drawer) open();
        throw err;
      });
  }

  function change(line, quantity) {
    var body = { quantity: Math.max(0, parseInt(quantity, 10) || 0), sections: sectionIds() };
    if (/^\d+$/.test(String(line))) body.line = Number(line); else body.id = line;
    clearErrors();
    setBusy(true);
    return request("/cart/change.js", body)
      .then(function (cart) {
        setBusy(false);
        return applyCart(cart, cart.sections, "change");
      })
      .catch(function (err) {
        setBusy(false);
        showError(err);
        return refresh().then(function () { throw err; });
      });
  }

  function update(updates) {
    clearErrors();
    setBusy(true);
    return request("/cart/update.js", { updates: updates, sections: sectionIds() })
      .then(function (cart) {
        setBusy(false);
        return applyCart(cart, cart.sections, "update");
      })
      .catch(function (err) {
        setBusy(false);
        showError(err);
        throw err;
      });
  }

  /* ---------------- drawer open and close (focus handled) ---------------- */
  var returnFocus = null;
  if (drawer) drawer.inert = true;
  function isOpen() { return document.body.classList.contains("cart-open"); }
  function open() {
    if (!drawer || isOpen()) return;
    returnFocus = document.activeElement;
    document.body.classList.add("cart-open");
    drawer.setAttribute("aria-hidden", "false");
    drawer.inert = false;
    var closeBtn = drawer.querySelector("[data-cart-close]");
    if (closeBtn) closeBtn.focus({ preventScroll: true });
    emit("pc:cart:open");
  }
  function close() {
    if (!isOpen()) return;
    document.body.classList.remove("cart-open");
    if (!drawer) return;
    var hadFocus = drawer.contains(document.activeElement);
    drawer.setAttribute("aria-hidden", "true");
    drawer.inert = true;
    if (hadFocus && returnFocus && document.contains(returnFocus)) returnFocus.focus({ preventScroll: true });
    returnFocus = null;
    emit("pc:cart:close");
  }

  /* ---------------- wiring, delegated so re-rendered markup keeps working ---------------- */
  document.addEventListener("click", function (e) {
    var t = e.target.closest ? e.target : null;
    if (!t) return;
    var el;
    if ((el = t.closest("[data-cart-open]"))) { e.preventDefault(); open(); return; }
    if ((el = t.closest("[data-cart-close]"))) { e.preventDefault(); close(); return; }
    if (overlay && t === overlay) { close(); return; }
    if ((el = t.closest("[data-cart-continue]"))) { close(); return; }
    if ((el = t.closest("[data-cart-remove]"))) {
      e.preventDefault();
      if (busy) return;
      change(el.getAttribute("data-line"), 0).catch(function () {});
      return;
    }
    if ((el = t.closest("[data-cart-qty]"))) {
      e.preventDefault();
      if (busy) return;
      var current = parseInt(el.getAttribute("data-quantity"), 10) || 0;
      var d = parseInt(el.getAttribute("data-d"), 10) || 0;
      change(el.getAttribute("data-line"), current + d).catch(function () {});
      return;
    }
    if ((el = t.closest("[data-pc-add]"))) {
      e.preventDefault();
      if (el.getAttribute("aria-busy") === "true") return;
      var variantId = el.getAttribute("data-variant-id");
      if (!variantId) return;
      var qty = parseInt(el.getAttribute("data-quantity"), 10) || 1;
      var qtyInput = el.getAttribute("data-qty-from") ? document.querySelector(el.getAttribute("data-qty-from")) : null;
      if (qtyInput) qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      var props = null;
      try { props = el.getAttribute("data-properties") ? JSON.parse(el.getAttribute("data-properties")) : null; } catch (err) { props = null; }
      el.setAttribute("aria-busy", "true");
      var errorTarget = el.getAttribute("data-error-target") ? document.querySelector(el.getAttribute("data-error-target")) : null;
      add([{ id: variantId, quantity: qty, properties: props }], { message: el.getAttribute("data-added-message") || undefined })
        .catch(function (err) { if (errorTarget) errorTarget.textContent = err.description || err.message; })
        .then(function () { el.removeAttribute("aria-busy"); });
      return;
    }
  });

  document.addEventListener("submit", function (e) {
    var form = e.target;
    if (!form.matches || !form.matches("form[data-pc-add-form]")) return;
    e.preventDefault();
    if (busy) return;
    var button = form.querySelector("[type=submit]");
    if (button) button.setAttribute("aria-busy", "true");
    addForm(form).catch(function () {}).then(function () { if (button) button.removeAttribute("aria-busy"); });
  });

  var inputTimer = null;
  document.addEventListener("change", function (e) {
    var input = e.target;
    if (!input.matches || !input.matches("input[data-cart-line-qty]")) return;
    clearTimeout(inputTimer);
    var line = input.getAttribute("data-line");
    var qty = Math.max(0, parseInt(input.value, 10) || 0);
    inputTimer = setTimeout(function () { change(line, qty).catch(function () {}); }, 150);
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && isOpen()) close();
  });

  /* the theme editor re-renders the drawer section: keep it inert and closed */
  document.addEventListener("shopify:section:load", function (e) {
    var fresh = e.target.querySelector("[data-cart-drawer]");
    if (fresh) { drawer = fresh; drawer.inert = !isOpen(); }
  });

  window.PCCart = {
    add: add,
    addForm: addForm,
    change: change,
    update: update,
    refresh: refresh,
    open: open,
    close: close,
    isOpen: isOpen,
    get: function () { return lastCart; },
    formatMoney: formatMoney
  };

  /* initial count from the server-rendered page is already right; fetch once so
     scripts that need the JSON have it without a round trip of their own */
  fetchCart().then(function (cart) {
    lastCart = cart;
    setCount(cart);
    emit("pc:cart:updated", { cart: cart, source: "init" });
  }).catch(function () {});
})();
